// src/api/signatures.js
import { getConfig } from "../config/index";
import { httpRequest } from "../utils/httpClient";
import { ApacuanaAPIError } from "../errors";
import ApacuanaSuccess from "../success";
import helpers from "../utils/helpers";
import { INTEGRATION_TYPE } from "../utils/constant";

/** @typedef {import("../types/signatures").Signer} Signer */
/** @typedef {import("../types/signatures").SignerData} SignerData */
/** @typedef {import("../types/signatures").AddSignerResponse} AddSignerResponse */
/** @typedef {import("../types/signatures").GetDigestData} GetDigestData */
/** @typedef {import("../types/signatures").GetDigestResponse} GetDigestResponse */
/** @typedef {import("../types/signatures").GetDocsParams} GetDocsParams */
/** @typedef {import("../types/signatures").GetDocsResponse} GetDocsResponse */
/** @typedef {import("../types/signatures").SignDocumentData} SignDocumentData */
/** @typedef {import("../types/signatures").SignaturePosition} SignaturePosition */
/** @typedef {import("../types/signatures").OnboardingSignerData} OnboardingSignerData */

// =================================================================
// Internal Functions
// =================================================================

const signDocumentOnBoarding = async (signData) => {
  const payload = helpers.validateOnBoardingSignDocumentData(signData);

  const { signature } = signData;
  try {
    const response = await httpRequest(
      `services/api/documents/sign/${signature.id}`,
      payload,
      "PUT"
    );
    return new ApacuanaSuccess(response);
  } catch (error) {
    if (error instanceof ApacuanaAPIError) {
      throw error;
    }
    throw new ApacuanaAPIError(
      `Failed to sign document: ${error.message || "Unknown error"}`
    );
  }
};

const signDocumentOnPremise = async () => {
  throw new ApacuanaAPIError(
    "Document signing is not supported for integration type: ONPREMISE",
    501,
    "NOT_IMPLEMENTED"
  );
};

const getDigestToSignOnBoarding = async (signData) => {
  try {
    const formData = helpers.createPayloadGetDigest(signData);
    const response = await httpRequest(
      `services/api/documents/getdigest/${signData.signatureId}`,
      formData,
      "POST"
    );
    const digest = response.data?.digest || response.digest;
    if (!digest) {
      throw new ApacuanaAPIError(
        "Signature generation failed: digest not found in the response.",
        500,
        "API_RESPONSE_ERROR"
      );
    }

    return new ApacuanaSuccess({ digest });
  } catch (error) {
    if (error.name === "ApacuanaAPIError") {
      throw error;
    }
    throw new ApacuanaAPIError(
      `Failed to sign document (on-boarding): ${error.message}`
    );
  }
};

const getDigestToSignOnPremise = async () => {
  throw new ApacuanaAPIError(
    "Digest retrieval is not supported for integration type: ONPREMISE",
    501,
    "NOT_IMPLEMENTED"
  );
};

const addSignerOnBoarding = async (signerData) => {
  const payload = helpers.validateOnBoardingSignerData(signerData);
  try {
    const response = await httpRequest(
      "services/api/documents/signingsdk",
      payload,
      "POST"
    );
    return new ApacuanaSuccess({
      signer: signerData.typedoc + signerData.doc,
      docId: response.id,
    });
  } catch (error) {
    if (error instanceof ApacuanaAPIError) {
      throw error;
    }
    throw new Error(`Failed to add signer in On-Boarding: ${error.message}`);
  }
};

const addSignerOnPremise = async () => {
  throw new ApacuanaAPIError(
    "Adding signers is not supported for integration type: ONPREMISE",
    501,
    "NOT_IMPLEMENTED"
  );
};

const getDocsOnPremise = async () => {
  throw new ApacuanaAPIError(
    "Document retrieval is not supported for integration type: ONPREMISE",
    501,
    "NOT_IMPLEMENTED"
  );
};

const getDocsOnBoarding = async (data) => {
  const { customerId } = getConfig();
  if (!customerId) {
    throw new ApacuanaAPIError(
      "'customerId' is not configured. Please configure the SDK.",
      400,
      "CONFIGURATION_ERROR"
    );
  }

  try {
    const params = new URLSearchParams({
      page: data.page,
      customerid: customerId,
      size: data.size,
    });

    if (typeof data.status !== "undefined") {
      params.append("status", data.status);
    }

    const apiUrl = `services/api/documents/listcustomer?${params.toString()}`;
    const response = await httpRequest(apiUrl, {}, "GET");
    return new ApacuanaSuccess({
      totalRecords: response.numofrecords,
      records: response.records,
    });
  } catch (error) {
    if (error.name === "ApacuanaAPIError") {
      throw error;
    }
    throw new ApacuanaAPIError(
      `Failed to get document list (on-boarding): ${error.message}`
    );
  }
};

const uploadSignatureVariantOnBoarding = async ({ file }) => {
  try {
    const response = await httpRequest(
      "services/api/customer/signaturephoto",
      { file },
      "POST"
    );
    return new ApacuanaSuccess(response);
  } catch (error) {
    if (error instanceof ApacuanaAPIError) {
      throw error;
    }
    throw new ApacuanaAPIError(
      `Failed to upload signature variant: ${error.message || "Unknown error"}`
    );
  }
};

const uploadSignatureVariantOnPremise = async () => {
  throw new ApacuanaAPIError(
    "Uploading signature variants is not supported for integration type: ONPREMISE",
    501,
    "NOT_IMPLEMENTED"
  );
};

const getSignatureVariantOnBoarding = async () => {
  const { customerId } = getConfig();
  try {
    const response = await httpRequest(
      `services/api/customer/getsignaturephotosdk/${customerId}`,
      {},
      "GET"
    );
    return new ApacuanaSuccess(response);
  } catch (error) {
    if (error instanceof ApacuanaAPIError) {
      throw error;
    }
    throw new ApacuanaAPIError(
      `Failed to get signature variant: ${error.message || "Unknown error"}`
    );
  }
};

const getSignatureVariantOnPremise = async () => {
  throw new ApacuanaAPIError(
    "Getting signature variants is not supported for integration type: ONPREMISE",
    501,
    "NOT_IMPLEMENTED"
  );
};

const deleteSignatureVariantOnBoarding = async () => {
  try {
    const response = await httpRequest(
      "services/api/customer/cleansignaturephoto",
      {},
      "DELETE"
    );
    return new ApacuanaSuccess(response);
  } catch (error) {
    if (error instanceof ApacuanaAPIError) {
      throw error;
    }
    throw new ApacuanaAPIError(
      `Failed to delete signature variant: ${error.message || "Unknown error"}`
    );
  }
};

const deleteSignatureVariantOnPremise = async () => {
  throw new ApacuanaAPIError(
    "Deleting signature variants is not supported for integration type: ONPREMISE",
    501,
    "NOT_IMPLEMENTED"
  );
};

// =================================================================
// Exported Functions
// =================================================================

/**
 * Firma un documento PDF con un certificado digital.
 * NOTA: Esta función no está implementada actualmente para ningún tipo de integración.
 * @param {SignDocumentData} signData - Datos necesarios para la firma.
 * @returns {Promise<object>}
 * @throws {ApacuanaAPIError} Arroja un error 'NOT_IMPLEMENTED' para cualquier tipo de integración.
 */
export const signDocument = async (signData) => {
  const { integrationType } = getConfig();

  if (integrationType === INTEGRATION_TYPE.ONBOARDING) {
    return signDocumentOnBoarding(signData);
  }

  if (integrationType === INTEGRATION_TYPE.ONPREMISE) {
    return signDocumentOnPremise();
  }

  throw new ApacuanaAPIError(
    `Unsupported integration type: ${integrationType}`,
    400,
    "UNSUPPORTED_INTEGRATION_TYPE"
  );
};

/**
 * Obtiene el digest de un documento para ser firmado.
 * @param {GetDigestData} signData - Datos requeridos para obtener el digest.
 * @returns {Promise<GetDigestResponse>} Una promesa que resuelve a un objeto con el digest del documento.
 * @throws {ApacuanaAPIError} Si los datos de entrada son inválidos, la llamada a la API falla, o el tipo de integración no es soportado.
 */
export const getDigest = async (signData) => {
  helpers.validateGetDigestData(signData);

  const { integrationType } = getConfig();

  if (integrationType === INTEGRATION_TYPE.ONBOARDING) {
    return getDigestToSignOnBoarding(signData);
  }

  if (integrationType === INTEGRATION_TYPE.ONPREMISE) {
    return getDigestToSignOnPremise();
  }

  throw new ApacuanaAPIError(
    `Document retrieval is not supported for an unknown integration type: ${integrationType}`,
    501,
    "NOT_IMPLEMENTED"
  );
};

/**
 * Añade un firmante a un documento.
 * @param {SignerData} signerData - Los datos del firmante que se va a añadir.
 * @returns {Promise<AddSignerResponse>} Una promesa que resuelve a un objeto con el resultado de la operación.
 * @throws {ApacuanaAPIError} Si los datos del firmante son inválidos, la llamada a la API falla, o el tipo de integración no es soportado.
 */
export const addSigner = async (signerData) => {
  if (
    !signerData ||
    typeof signerData !== "object" ||
    Object.keys(signerData).length === 0
  ) {
    throw new ApacuanaAPIError(
      "Signer data (signerData) is required and must be a non-empty object.",
      400,
      "INVALID_PARAMETER"
    );
  }
  const { integrationType } = getConfig();

  if (integrationType === INTEGRATION_TYPE.ONBOARDING) {
    return addSignerOnBoarding(signerData);
  }

  if (integrationType === INTEGRATION_TYPE.ONPREMISE) {
    return addSignerOnPremise();
  }

  throw new ApacuanaAPIError(
    `Unsupported integration type: ${integrationType}`,
    400,
    "UNSUPPORTED_INTEGRATION_TYPE"
  );
};

/**
 * Obtiene una lista de documentos basada en los filtros especificados.
 * @param {GetDocsParams} data - Objeto con parámetros de paginación y filtros.
 * @returns {Promise<GetDocsResponse>} Una promesa que resuelve a un objeto con la lista de documentos.
 * @throws {ApacuanaAPIError} Si los parámetros de paginación son inválidos, 'customerId' no está configurado, la llamada a la API falla, o el tipo de integración no es soportado.
 */
export const getDocs = async (data) => {
  helpers.validateGetDocsData(data);

  const { integrationType } = getConfig();

  if (integrationType === INTEGRATION_TYPE.ONBOARDING) {
    return getDocsOnBoarding(data);
  }

  if (integrationType === INTEGRATION_TYPE.ONPREMISE) {
    return getDocsOnPremise();
  }

  throw new ApacuanaAPIError(
    `Document retrieval is not supported for an unknown integration type: ${integrationType}`,
    501,
    "NOT_IMPLEMENTED"
  );
};

/**
 * Sube una variante de firma para un firmante.
 * @param {object} data - Datos para la subida.
 * @param {File} data.file - El archivo de la variante de firma (debe ser PNG).
 * @returns {Promise<object>} Una promesa que resuelve a un objeto con el resultado de la operación.
 * @throws {ApacuanaAPIError} Si los datos son inválidos o la llamada a la API falla.
 */
export const uploadSignatureVariant = async (data) => {
  if (!data || !data.file) {
    throw new ApacuanaAPIError(
      "El parámetro 'file' es requerido.",
      400,
      "INVALID_PARAMETER"
    );
  }

  if (typeof File === "undefined" || !(data.file instanceof File)) {
    throw new ApacuanaAPIError(
      "El parámetro 'file' debe ser una instancia de 'File'.",
      400,
      "INVALID_PARAMETER"
    );
  }

  if (data.file.type !== "image/png") {
    throw new ApacuanaAPIError(
      "El archivo debe ser de tipo PNG (image/png).",
      400,
      "INVALID_PARAMETER"
    );
  }

  const { integrationType } = getConfig();

  if (integrationType === INTEGRATION_TYPE.ONBOARDING) {
    return uploadSignatureVariantOnBoarding(data);
  }

  if (integrationType === INTEGRATION_TYPE.ONPREMISE) {
    return uploadSignatureVariantOnPremise();
  }

  throw new ApacuanaAPIError(
    `Unsupported integration type: ${integrationType}`,
    400,
    "UNSUPPORTED_INTEGRATION_TYPE"
  );
};

/**
 * Obtiene la variante de firma del firmante.
 * @returns {Promise<object>} Una promesa que resuelve a un objeto con los datos de la variante de firma.
 * @throws {ApacuanaAPIError} Si la llamada a la API falla o el tipo de integración no es soportado.
 */
export const getSignatureVariant = async () => {
  const { integrationType } = getConfig();

  if (integrationType === INTEGRATION_TYPE.ONBOARDING) {
    return getSignatureVariantOnBoarding();
  }

  if (integrationType === INTEGRATION_TYPE.ONPREMISE) {
    return getSignatureVariantOnPremise();
  }

  throw new ApacuanaAPIError(
    `Unsupported integration type: ${integrationType}`,
    400,
    "UNSUPPORTED_INTEGRATION_TYPE"
  );
};

/**
 * Elimina la variante de firma del firmante.
 * @returns {Promise<object>} Una promesa que resuelve a un objeto con el resultado de la operación.
 * @throws {ApacuanaAPIError} Si la llamada a la API falla o el tipo de integración no es soportado.
 */
export const deleteSignatureVariant = async () => {
  const { integrationType } = getConfig();

  if (integrationType === INTEGRATION_TYPE.ONBOARDING) {
    return deleteSignatureVariantOnBoarding();
  }

  if (integrationType === INTEGRATION_TYPE.ONPREMISE) {
    return deleteSignatureVariantOnPremise();
  }

  throw new ApacuanaAPIError(
    `Unsupported integration type: ${integrationType}`,
    400,
    "UNSUPPORTED_INTEGRATION_TYPE"
  );
};
